/**
 * ConfigurationRequiredWidget — Phase 47
 * =======================================
 * Dashboard widget that surfaces shipments stuck in draft /
 * configuration_required / ready lifecycle states. Operators must finish
 * setup + click Activate Monitoring BEFORE the platform dispatches alerts.
 *
 * Data: GET /api/lifecycle/dashboard
 *   { counts: {...}, needs_attention: [...], total_needs_attention: N }
 *
 * Behaviour:
 *   - Hidden when total_needs_attention === 0 (all clear).
 *   - Header: pill counts by state.
 *   - Body: top 5 shipments with name + state + readiness % + missing items.
 *   - Click a row → onSelectShipment(id) routes to shipment detail page,
 *     where the LifecycleBanner allows the operator to finish setup.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Warning, Lock, CheckCircle, CaretRight, Lightning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_PILL = {
  draft:                  { label: "DRAFT",         color: "text-zinc-300",  bg: "bg-zinc-500/15"  },
  configuration_required: { label: "CONFIG REQ",    color: "text-amber-300", bg: "bg-amber-500/15" },
  ready:                  { label: "READY",         color: "text-teal-300",  bg: "bg-teal-500/15"  },
};

export default function ConfigurationRequiredWidget({ onSelectShipment }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/lifecycle/dashboard`);
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return null;
  if (!data || data.total_needs_attention === 0) return null;

  const counts = data.counts || {};
  const rows = (data.needs_attention || []).slice(0, 5);

  return (
    <Card className="bg-zinc-900 border-amber-700/40 p-5" data-testid="config-required-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-sm bg-amber-500/15">
            <Warning size={16} weight="duotone" className="text-amber-400" />
          </div>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
            Configuration Required
          </h3>
          <span className="text-[10px] text-zinc-500">
            · Alerts will NOT dispatch until shipments are activated
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <StatePill state="draft" count={counts.draft || 0} />
          <StatePill state="configuration_required" count={counts.configuration_required || 0} />
          <StatePill state="ready" count={counts.ready || 0} />
          <span className="mx-1 text-zinc-700">|</span>
          <span className="text-[10px] text-emerald-300 flex items-center gap-1">
            <CheckCircle size={12} weight="fill" />
            {counts.active || 0} active
          </span>
        </div>
      </div>

      <div className="space-y-1.5" data-testid="config-required-rows">
        {rows.map((s) => (
          <button
            key={s.id}
            onClick={() => onSelectShipment?.(s.id)}
            className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-sm border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/40 transition-colors"
            data-testid={`config-row-${s.id.slice(0, 8)}`}
          >
            <Lock size={12} weight="duotone" className="text-amber-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-zinc-200 truncate">{s.name}</span>
                <StatePill state={s.lifecycle_state} solo />
              </div>
              {s.missing?.length > 0 && (
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  Missing: {s.missing.join(" · ")}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`text-xs font-mono ${s.readiness_score >= 75 ? "text-teal-300" : s.readiness_score >= 50 ? "text-amber-300" : "text-red-300"}`}>
                {s.readiness_score}%
              </span>
              <CaretRight size={11} className="text-zinc-600" />
            </div>
          </button>
        ))}
      </div>

      {data.total_needs_attention > rows.length && (
        <div className="mt-3 text-[10px] text-zinc-500 text-center">
          + {data.total_needs_attention - rows.length} more — view all in Shipments
        </div>
      )}
    </Card>
  );
}

function StatePill({ state, count, solo }) {
  const meta = STATE_PILL[state] || STATE_PILL.draft;
  if (solo) {
    return (
      <span className={`text-[9px] uppercase tracking-wider font-bold ${meta.color} ${meta.bg} rounded px-1.5 py-0.5`}>
        {meta.label}
      </span>
    );
  }
  return (
    <span className={`text-[10px] ${meta.color} ${meta.bg} rounded px-1.5 py-0.5 flex items-center gap-1`}>
      <span className="font-mono font-semibold">{count}</span>
      <span className="opacity-70">{meta.label}</span>
    </span>
  );
}
